export type Platform = 'ios' | 'android' | 'desktop';

// What onboarding step 7 needs to know about the running app (UI architecture §12).
export const runtime = {
  // Installed to the home screen; iOS Safari also reports it as navigator.standalone.
  get isStandalone(): boolean {
    return (
      matchMedia('(display-mode: standalone)').matches ||
      ('standalone' in navigator && navigator.standalone === true)
    );
  },
  get platform(): Platform {
    const { userAgent, maxTouchPoints } = navigator;
    // iPadOS Safari presents itself as a Mac; only the touch points tell it apart.
    if (/iPhone|iPad|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1)) {
      return 'ios';
    }
    return /Android/.test(userAgent) ? 'android' : 'desktop';
  },
};

// Chrome's event on Android and desktop; not in the DOM types, and iOS has none.
type BeforeInstallPromptEvent = Event & {
  prompt(): Promise<unknown>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let captured: BeforeInstallPromptEvent | null = null;

function keep(event: Event) {
  // No browser mini-infobar: the install button of step 7 fires the prompt.
  event.preventDefault();
  captured = event as BeforeInstallPromptEvent;
}

// The beforeinstallprompt capture (UI architecture §12). capture() returns its release.
export const installPrompt = {
  capture(): () => void {
    window.addEventListener('beforeinstallprompt', keep);
    return () => window.removeEventListener('beforeinstallprompt', keep);
  },
  get available(): boolean {
    return captured !== null;
  },
  async prompt(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    if (captured === null) return 'unavailable';
    // An event prompts once; the next offer needs a new event.
    const event = captured;
    captured = null;
    await event.prompt();
    return (await event.userChoice).outcome;
  },
};
